import { AboutPage } from 'modules/about';
import AnalyticsPage from 'modules/analytics/ui/pages/analytics-page';
import ApplicationLinkPage from 'modules/application-link/ui/pages/application-link-page';
import ApplicationSuccessPage from 'modules/application-link/ui/pages/application-success-page';
import AuthPagesSelectTournament from 'modules/auth/ui/pages/select-tournament/select-tournament';
import SignInPage from 'modules/auth/ui/pages/sign-in-page';
import SignUpPage from 'modules/auth/ui/pages/sign-up-page';
import BoardPage from 'modules/board-master/ui/pages/board/board-page';
import MasterBoardPage from 'modules/board-master/ui/pages/main/master-board-page';
import PanelPage from 'modules/board-master/ui/pages/panel/panel-page';
import ClientChampionshipPage from 'modules/client/tournaments/ui/pages/client-championship-page';
import ChatPage from 'modules/chat/ui/pages/chat-page';
import DevicesPage from 'modules/devices/ui/pages/devices-page/devices-page';
import LocationCreatePage from 'modules/locations/ui/pages/location-create-page';
import LocationViewPage from 'modules/locations/ui/pages/location-view-page';
import LocationsListPage from 'modules/locations/ui/pages/locations-list-pages';
import MainPage from 'modules/main/ui/pages/main-page/main-page';
import MediaManagerEditorPage from 'modules/manager/ui/pages/media-manager-editor-page';
import MediaManagerPresetsPage from 'modules/manager/ui/pages/media-manager-presets-page';
import MediaManagerTemplatesListPage from 'modules/manager/ui/pages/media-manager-templates-list-page';
import { MigrationPage } from 'modules/migration';
import FeatureRoute from 'modules/navigation/ui/components/feature-route';
import LegacyTourneysRedirect from 'modules/navigation/ui/components/legacy-tourneys-redirect';
import NotificationsPage from 'modules/notifications/ui/pages/notifications-page';
import CreateNotificationPage from 'modules/notifications/ui/pages/create';
import EditNotificationPage from 'modules/notifications/ui/pages/edit';
import OrganizerCreatePage from 'modules/organizer/ui/pages/organizer-create-page';
import OrganizerCard from 'modules/organizers/ui/pages/organizer-card';
import OrganizersList from 'modules/organizers/ui/pages/organizers-list';
import PlansDetailPage from 'modules/plans/ui/pages/plans-detail-page';
import PlansListPage from 'modules/plans/ui/pages/plans-list-page';
import FullSizeControlPanel from 'modules/overlay-v2/ui/page/full-size-control-panel';
import OverlayV2 from 'modules/overlay-v2/ui/page/overlayV2';
import PlayerCreatePage from 'modules/players/ui/pages/player-create-page';
import PlayerViewPage from 'modules/players/ui/pages/player-view-page';
import PlayerListPage from 'modules/players/ui/pages/players-list-page';
import { UsersPagesCacheProvider } from 'modules/roles-manager/ui/hooks/use-roles-manager-provider';
import RolesManagerPage from 'modules/roles-manager/ui/pages/roles-manager/roles-manager-page';
import Calendar from 'modules/schedule/ui/pages/calendar';
import SettingsPage from 'modules/settings/ui/pages/settings-page/settings-page';
import TeamsEditPage from 'modules/teams/ui/pages/team-edit-page';
import TeamsCreatePage from 'modules/teams/ui/pages/teams-create-page';
import TeamsPage from 'modules/teams/ui/pages/teams-page';
import BoardEditor from 'modules/template-editor/ui/pages/board-editor';
import BoardPreview from 'modules/template-editor/ui/pages/board-preview';
import Boards from 'modules/template-editor/ui/pages/boards';
import Catalog from 'modules/template-editor/ui/pages/catalog';
import CatalogCard from 'modules/template-editor/ui/pages/catalog-card';
import Templates from 'modules/template-editor/ui/pages/templates';
import ChampionshipCreatePage from 'modules/championship/ui/pages/championship-create-page';
import ChampionshipEditPage from 'modules/championship/ui/pages/championship-edit-page';
import ChampionshipsListPage from 'modules/championship/ui/pages/championships-list-page';
import type { appFeature } from 'modules/organizer/domain/interfaces/available-feature';
import React from 'react';
import { Route, Routes } from 'react-router-dom';
import { checkIsDev } from 'shared/utils/check-is-dev';
import UDMainLayout from 'modules/app-shell/ui/layout';
import { MainLayoutProvider } from 'modules/app-shell/ui/layout/context';
import { QueryParamProvider } from 'use-query-params';
import { Pages } from './domain/enums/pages';
import MobileGuard from './ui/components/mobile-guard';
import SecureRoute from './ui/components/secure-route';
import RouteAdapter from './utils/route-adapter';

/**
 * Витрина кита — единственная страница, которая грузится лениво: демо тянут за собой
 * моки, канву и примеры, и в основном чанке им делать нечего. Условная регистрация
 * роута сама по себе импорт не выкидывает, поэтому именно `React.lazy`, а `<Suspense>`
 * ниже обязателен — без него переход падает в рантайме.
 */
const UiKitPage = React.lazy(() => import('modules/ui-kit/ui/pages/ui-kit-page'));

interface LayoutRoute {
  /** Always built from the `Pages` enum — never a hardcoded string. */
  path: string;
  /** The page itself; the guards around it come from the fields below. */
  element: JSX.Element;
  /** `SecureRoute` props. Defaults match the component: tournament required, no admin check. */
  tournamentRequired?: boolean;
  adminOnly?: boolean;
  /** Wraps the page in `FeatureRoute`, so a disabled feature redirects away. */
  feature?: appFeature;
}

/**
 * Pages rendered inside the main layout (sidebar + header). Every one of them
 * is behind `SecureRoute` and `MobileGuard`; the table only records what
 * differs. Add a page here rather than hand-writing another `<Route>` — the
 * `/create-page` skill does exactly that.
 */
const LAYOUT_ROUTES: LayoutRoute[] = [
  { path: Pages.INDEX, element: <MainPage /> },
  { path: `${Pages.SETTINGS.INDEX}/*`, element: <SettingsPage />, tournamentRequired: false },
  { path: Pages.CHAMPIONSHIPS.INDEX, element: <ChampionshipsListPage /> },
  { path: Pages.CHAMPIONSHIPS.CREATE, element: <ChampionshipCreatePage /> },
  { path: `${Pages.CHAMPIONSHIPS.EDIT}/*`, element: <ChampionshipEditPage /> },
  { path: Pages.TEAMS.INDEX, element: <TeamsPage /> },
  { path: Pages.TEAMS.CREATE, element: <TeamsCreatePage /> },
  { path: `${Pages.TEAMS.EDIT}/*`, element: <TeamsEditPage /> },
  { path: `${Pages.TEMPLATE.INDEX}/*`, element: <Templates /> },
  { path: `${Pages.TEMPLATE.BOARDS}/*`, element: <Boards /> },
  { path: `${Pages.TEMPLATE.CATALOG.INDEX}/*`, element: <Catalog /> },
  { path: `${Pages.TEMPLATE.CATALOG.CARD}/*`, element: <CatalogCard /> },
  { path: Pages.ORGANIZERS.INDEX, element: <OrganizersList />, tournamentRequired: false, adminOnly: true },
  { path: Pages.ORGANIZERS.VIEW, element: <OrganizerCard /> },
  { path: Pages.PLANS.INDEX, element: <PlansListPage />, tournamentRequired: false, adminOnly: true },
  { path: Pages.PLANS.VIEW, element: <PlansDetailPage />, tournamentRequired: false, adminOnly: true },
  { path: Pages.SCHEDULE.INDEX, element: <Calendar /> },
  { path: Pages.PLAYERS.INDEX, element: <PlayerListPage /> },
  { path: `${Pages.PLAYERS.VIEW}/*`, element: <PlayerViewPage /> },
  { path: `${Pages.PLAYERS.CREATE}/*`, element: <PlayerCreatePage /> },
  { path: Pages.LOCATIONS.INDEX, element: <LocationsListPage /> },
  { path: Pages.LOCATIONS.CREATE, element: <LocationCreatePage /> },
  { path: `${Pages.LOCATIONS.VIEW}/*`, element: <LocationViewPage /> },
  // Организации у пользователя ещё нет — иначе он бы сюда не шёл. С дефолтным
  // `tournamentRequired` guard уводил его на `/settings`, откуда пустой профиль
  // и предлагает создать организацию: единственный выход замыкался сам на себя.
  { path: Pages.ORGANIZER.CREATE, element: <OrganizerCreatePage />, tournamentRequired: false },
  { path: Pages.NOTIFICATIONS.INDEX, element: <NotificationsPage /> },
  { path: Pages.NOTIFICATIONS.CREATE, element: <CreateNotificationPage />, adminOnly: true },
  { path: Pages.NOTIFICATIONS.EDIT, element: <EditNotificationPage />, adminOnly: true },
  { path: Pages.ABOUT, element: <AboutPage />, tournamentRequired: false },
  { path: Pages.MIGRATION.INDEX, element: <MigrationPage />, tournamentRequired: false, adminOnly: true },
  {
    path: Pages.ROLES_MANAGER.INDEX,
    element: (
      <UsersPagesCacheProvider>
        <RolesManagerPage />
      </UsersPagesCacheProvider>
    ),
  },
  { path: Pages.DEVICES.INDEX, element: <DevicesPage /> },
  { path: Pages.MEDIA_MANAGER.INDEX, element: <MediaManagerTemplatesListPage />, feature: 'MEDIA_MANAGER' },
  { path: Pages.MEDIA_MANAGER.TEMPLATES_LIBRARY, element: <MediaManagerPresetsPage />, feature: 'MEDIA_MANAGER' },
  { path: Pages.ANALYTICS.INDEX, element: <AnalyticsPage />, feature: 'TOURNAMENT_ANALYTICS' },
  { path: Pages.CHAT.INDEX, element: <ChatPage /> },
];

/**
 * Раздел `/ui-kit` — dev-only: в проде роут не регистрируется вовсе, а не просто
 * прячется из меню, иначе супер-админ открыл бы витрину по прямой ссылке.
 * `checkIsDev()` истинен и локально, и на TEST — там витрину смотрит дизайн.
 * Путь обязан быть с `/*`: точный `path` увёл бы `/ui-kit/forms/select` в catch-all.
 */
if (checkIsDev()) {
  LAYOUT_ROUTES.push({
    path: `${Pages.UI_KIT.INDEX}/*`,
    element: (
      <React.Suspense fallback={null}>
        <UiKitPage />
      </React.Suspense>
    ),
    tournamentRequired: false,
    adminOnly: true,
  });
}

/** SecureRoute → FeatureRoute (optional) → MobileGuard → page. */
function guardLayoutRoute(route: LayoutRoute): JSX.Element {
  const guarded = <MobileGuard>{route.element}</MobileGuard>;

  return (
    <SecureRoute tournamentRequired={route.tournamentRequired} adminOnly={route.adminOnly}>
      {route.feature ? <FeatureRoute feature={route.feature}>{guarded}</FeatureRoute> : guarded}
    </SecureRoute>
  );
}

const AppRouter = () => {
  return (
    <QueryParamProvider ReactRouterRoute={RouteAdapter}>
      <Routes>
        <Route
          path="/"
          element={
            <MobileGuard>
              <MainLayoutProvider>
                <UDMainLayout />
              </MainLayoutProvider>
            </MobileGuard>
          }
        >
          {LAYOUT_ROUTES.map((route) => (
            <Route key={route.path} path={route.path} element={guardLayoutRoute(route)} />
          ))}
        </Route>

        {/* Соревнования переехали с /tourneys на /championships — старые ссылки не должны ломаться. */}
        <Route path={Pages.LEGACY_TOURNEYS} element={<LegacyTourneysRedirect />} />
        <Route path={`${Pages.LEGACY_TOURNEYS}/*`} element={<LegacyTourneysRedirect />} />

        <Route path={Pages.SIGN_UP} element={<SignUpPage />} />
        <Route path={Pages.SIGN_IN} element={<SignInPage />} />
        <Route
          path={Pages.SELECT_TOURNAMENT}
          element={
            <MobileGuard>
              <AuthPagesSelectTournament />
            </MobileGuard>
          }
        />

        {/* Full-screen editors: secured, but outside the layout and without the mobile guard. */}
        {/* Редактор табло трансляции — только по подписке с OBS_INTEGRATION; без неё — обратно к списку шаблонов. */}
        <Route
          path={`${Pages.TEMPLATE.EDITOR}/*`}
          element={
            <SecureRoute>
              <FeatureRoute feature={'OBS_INTEGRATION'} fallback={Pages.TEMPLATE.INDEX}>
                <BoardEditor />
              </FeatureRoute>
            </SecureRoute>
          }
        />
        <Route
          path={`${Pages.TEMPLATE.PREVIEW}/*`}
          element={
            <SecureRoute>
              <FeatureRoute feature={'OBS_INTEGRATION'} fallback={Pages.TEMPLATE.INDEX}>
                <BoardPreview />
              </FeatureRoute>
            </SecureRoute>
          }
        />
        <Route
          path={Pages.MEDIA_MANAGER.EDITOR}
          element={
            <SecureRoute>
              <FeatureRoute feature={'MEDIA_MANAGER'}>
                <MediaManagerEditorPage />
              </FeatureRoute>
            </SecureRoute>
          }
        />

        <Route path={`${Pages.OVERLAY_V2.INDEX}/*`} element={<OverlayV2 />} />
        <Route path={`${Pages.OVERLAY_V2.CONTROL_PANEL}/*`} element={<FullSizeControlPanel />} />

        <Route path={`${Pages.MASTER_BOARDS.INDEX}`} element={<MasterBoardPage />} />
        <Route path={`${Pages.MASTER_BOARDS.CONTROL_PANEL}`} element={<PanelPage />} />
        <Route path={`${Pages.MASTER_BOARDS.BOARD}`} element={<BoardPage />} />

        <Route path={Pages.APPLICATION_LINK.INDEX} element={<ApplicationLinkPage />} />
        <Route path={Pages.APPLICATION_LINK.SUCCESS} element={<ApplicationSuccessPage />} />

        <Route path={Pages.CLIENT_TOURNAMENT.VIEW_CHAMPIONSHIP} element={<ClientChampionshipPage />} />
      </Routes>
    </QueryParamProvider>
  );
};

// Memoize the AppRouter component to prevent unnecessary re-renders
export default React.memo(AppRouter);
